import {Color} from '@benchling/nucleus-ui';
import {Box, createStyles, Text} from '@mantine/core';
import classNames from 'classnames';
import React from 'react';

import {BaseCellError, TriangleState} from 'notebook/tables/errors/TableErrorConstants';
import {hasAnyBlockingErrors, sortErrors} from 'notebook/tables/excess/Util';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';

import {EvaluatedCellError} from '../excess/EvaluationConstants';

type VisibleTriangleState = Exclude<TriangleState, TriangleState.VALID_NO_TRIANGLE>;
type ErrorTone = 'danger' | 'info' | 'outdated' | 'warning';

const ERROR_TONES: Record<VisibleTriangleState, ErrorTone> = {
  [TriangleState.VALID_WITH_TRIANGLE]: 'info',
  [TriangleState.INVALID]: 'warning',
  [TriangleState.PARTIALLY_VALID]: 'warning',
  [TriangleState.ILLEGAL]: 'danger',
  [TriangleState.OUTDATED]: 'outdated',
  [TriangleState.WARNING]: 'warning',
  [TriangleState.UNSUBMITTED_CHANGES]: 'info',
};

const COLOR_TONES: Record<ErrorTone, string> = {
  info: Color.BLUE_300,
  warning: Color.ORANGE_350,
  danger: Color.RED_400,
  outdated: Color.GRAPE_400,
};

// Alpha for section tint backgrounds — low enough to feel like a hint, not a fill.
const SECTION_BG_ALPHA = 0.08;

const useStyles = createStyles((theme) => ({
  container: {display: 'flex', flexDirection: 'column'},
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: theme.spacing.sm,
  },
  divider: {borderTop: `1px solid ${theme.colors.gray[3]}`},
  labelChip: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    borderRadius: theme.radius.md,
    display: 'inline-flex',
    fontSize: theme.fontSizes.xs,
    fontWeight: 700,
    gap: 4,
    lineHeight: 1,
    padding: `3px ${theme.spacing.xs}`,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap' as const,
  },
  resolutionButton: {
    alignSelf: 'flex-start',
    marginTop: theme.spacing.xs,
    whiteSpace: 'nowrap' as const,
  },
}));

interface Props {
  error: EvaluatedCellError;
  // This serves as the default label class for the errors.
  // If individual errors have an errorState, then that will be used to determine the label color
  errorState: VisibleTriangleState;
  children: JSX.Element;
  placement?: FloatingPlacement;
  showResolutionButtons?: boolean;
}

function getErrorTone(errorState: TriangleState | undefined, fallbackTone: ErrorTone): ErrorTone {
  if (!errorState || errorState === TriangleState.VALID_NO_TRIANGLE) {
    return fallbackTone;
  }
  return ERROR_TONES[errorState as VisibleTriangleState] ?? fallbackTone;
}

function chipSx(_theme: unknown, tone: ErrorTone): {backgroundColor: string; color: string} {
  return {backgroundColor: COLOR_TONES[tone], color: '#ffffff'};
}

function sectionSx(
  theme: {fn: {rgba: (color: string, alpha: number) => string}},
  tone: ErrorTone
): {backgroundColor: string} {
  return {backgroundColor: theme.fn.rgba(COLOR_TONES[tone], SECTION_BG_ALPHA)};
}

function ErrorTrianglePopover(props: Props): JSX.Element {
  const {classes} = useStyles();
  const defaultTone = ERROR_TONES[props.errorState];
  const sortedErrors = sortErrors(props.error);
  const errors: Array<BaseCellError> = Array.isArray(sortedErrors) ? sortedErrors : [sortedErrors];
  const hasBlockingError = hasAnyBlockingErrors(errors);
  const [primaryError, ...secondaryErrors] = errors;
  const primaryTone = getErrorTone(primaryError.errorState, defaultTone);

  return (
    <Popover
      placement={props.placement}
      disableDefaultFloatingContentPadding={true}
      renderContent={() => (
        <div className={classes.container}>
          <Box className={classes.item}>
            {primaryError.type && (
              <Box className={classes.labelChip} sx={(theme) => chipSx(theme, primaryTone)}>
                <span>{primaryError.type}</span>
              </Box>
            )}
            {primaryError.message && <Text size="sm">{primaryError.message}</Text>}
            {props.showResolutionButtons !== false && !hasBlockingError && primaryError.resolutionAction && (
              <Button
                className={classes.resolutionButton}
                size={ButtonSize.MINI}
                buttonStyle={ButtonStyle.PRIMARY}
                onClick={primaryError.resolutionAction}
              >
                {primaryError.resolutionActionLabel ?? 'Resolve'}
              </Button>
            )}
          </Box>
          {secondaryErrors.map((error, index) => {
            const tone = getErrorTone(error.errorState, defaultTone);
            return (
              <Box
                className={classNames(classes.item, classes.divider)}
                key={`${error.type}-${error.message}-${index}`}
                sx={(theme) => sectionSx(theme, tone)}
              >
                <Text size="xs">
                  <Text component="span" size="xs" weight={700} sx={{textTransform: 'uppercase'}}>
                    {error.type}:&nbsp;
                  </Text>
                  {error.message}
                </Text>
              </Box>
            );
          })}
        </div>
      )}
    >
      {props.children}
    </Popover>
  );
}
ErrorTrianglePopover.displayName = 'ErrorTrianglePopover';

export default ErrorTrianglePopover;
